import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Monitor,
  Phone,
  Megaphone,
  FileCheck,
  BarChart3,
  ScanLine,
  ShieldCheck,
  CreditCard,
  UserRound,
  CalendarCheck,
  Pill,
  Bot,
  ArrowRight,
  Loader2,
  MapPin,
  Check,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";

const REFERRAL_SOURCE = "dr-keith-gressell";

const softwares = [
  { icon: Monitor, label: "Practice Management System" },
  { icon: Phone, label: "Communication Suite" },
  { icon: Megaphone, label: "Marketing Suite" },
  { icon: FileCheck, label: "eClaims Portal" },
  { icon: BarChart3, label: "Revenue Intelligence Suite" },
  { icon: ScanLine, label: "Cloud Imaging Software" },
  { icon: ShieldCheck, label: "Insurance Verification Software" },
  { icon: CreditCard, label: "Integrated Payments Suite" },
  { icon: UserRound, label: "Patient Portal" },
  { icon: Pill, label: "ePrescribe" },
  { icon: CalendarCheck, label: "Online Scheduling" },
];

const aiProducts = [
  "AI Receptionist",
  "AI Schedule Optimizer",
  "AI Hygiene Recall",
  "AI Patient Experience",
  "AI Case Acceptance",
  "AI Patient Intake",
  "AI Practice Manager",
  "AI Fax Manager",
  "AI Insurance Verification",
  "AI Claim Submission",
  "AI Claim Scrubbing",
  "AI Denial Management",
  "AI Patient Billing",
  "AI AR & Collections",
  "AI EOB Posting",
  "AI Insurance Calls",
  "AI Copilot",
  "AI Scribe",
  "AI Treatment Planner",
  "AI Revenue Analytics",
];

const credentials = [
  "DMD, Temple University '05",
  "Former U.S. Army dental officer",
  "Nobel Biocare certified implantologist",
  "Founder, Harbor Hill Dentistry & Airway Center",
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const DrGressellPage = () => {
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    practiceName: "",
    providers: "",
    role: "",
    message: "",
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSelectChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!formData.firstName || !formData.lastName || !formData.email ||
        !formData.phone || !formData.practiceName || !formData.providers || !formData.role) {
      setSubmitError("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);

    try {
      const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      const apiUrl = process.env.REACT_APP_API_URL || (isLocalhost ? "http://localhost:8001" : "");
      const response = await fetch(`${apiUrl}/api/demo-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, referralSource: REFERRAL_SOURCE }),
      });

      if (!response.ok) {
        throw new Error("Failed to submit form");
      }

      navigate("/schedule-demo");
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitError("There was an error submitting your request. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="gr-page min-h-screen bg-[#f7fafd] text-slate-900 antialiased selection:bg-blue-100 selection:text-blue-900">
      <style>{`
        .gr-eyebrow { letter-spacing: 0.18em; }
        .gr-dotgrid { background-image: radial-gradient(#cbd9ea 1px, transparent 1px); background-size: 26px 26px; }
        .gr-hairline { background: linear-gradient(to right, transparent, #bfd4ec, transparent); }
        @keyframes gr-ai-wave {
          0%, 7%, 100% { border-color: rgb(219 234 254 / 0.8); box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05); background: white; }
          3.5% { border-color: rgb(96 165 250); box-shadow: 0 0 0 3px rgb(59 130 246 / 0.12), 0 4px 16px -2px rgb(59 130 246 / 0.35); background: rgb(239 246 255); }
        }
        .gr-ai-chip { animation: gr-ai-wave 14s ease-in-out infinite; }
        @keyframes gr-glow-drift {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -24px) scale(1.08); }
          66% { transform: translate(-24px, 18px) scale(0.94); }
        }
        .gr-glow { animation: gr-glow-drift 9s ease-in-out infinite; }
        @keyframes gr-ping {
          0% { transform: scale(1); opacity: 0.8; }
          70%, 100% { transform: scale(2.6); opacity: 0; }
        }
        .gr-ping { animation: gr-ping 1.8s cubic-bezier(0, 0, 0.2, 1) infinite; }
      `}</style>

      {/* Top bar */}
      <header className="relative z-20 max-w-6xl mx-auto px-5 sm:px-8 pt-5 flex items-center justify-between">
        <a href="https://asha.dental" className="text-xl font-extrabold text-slate-900 tracking-tight">
          Asha <span className="text-blue-600">Dental</span>
        </a>
        <div className="flex items-center gap-2 rounded-full bg-white border border-blue-100 shadow-sm px-3.5 py-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
          <span className="text-[11px] sm:text-xs font-semibold text-slate-600">Referred by Dr. Keith Gressell</span>
        </div>
      </header>

      {/* ============ BIO HERO ============ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 gr-dotgrid opacity-40" />
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] bg-blue-200/40 rounded-full blur-[100px]" />
        <div className="absolute top-40 -left-40 w-[420px] h-[420px] bg-sky-100/60 rounded-full blur-[90px]" />

        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-10 sm:pt-14 pb-14 sm:pb-20">
          <div className="grid md:grid-cols-[340px_1fr] gap-8 lg:gap-12 items-center">
            {/* Photo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-w-[300px] sm:max-w-[340px] mx-auto md:mx-0 w-full"
            >
              <div className="absolute -inset-3 bg-gradient-to-br from-blue-200/60 to-sky-100/40 rounded-[28px] -rotate-2" />
              <div className="relative rounded-3xl overflow-hidden border-4 border-white shadow-xl shadow-blue-900/10 aspect-[4/5]">
                <img
                  src="/dr-gressell.webp"
                  alt="Dr. Keith Gressell"
                  className="w-full h-full object-cover object-[center_12%]"
                />
              </div>
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-full border border-blue-100 shadow-md px-4 py-1.5 whitespace-nowrap">
                <span className="text-xs font-bold text-blue-700">Harbor Hill Dentistry &amp; Airway Center</span>
              </div>
            </motion.div>

            {/* Bio */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="text-center md:text-left"
            >
              <p className="gr-eyebrow text-[11px] sm:text-xs font-bold uppercase text-blue-600 mb-3">
                A personal introduction from
              </p>
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
                Keith Gressell, <span className="text-blue-600">DMD</span>
              </h1>
              <div className="mt-2.5 flex items-center justify-center md:justify-start gap-1.5 text-sm font-medium text-slate-500">
                <MapPin className="w-4 h-4 text-blue-500" />
                Gig Harbor, Washington
              </div>

              <p className="mt-5 text-[15px] sm:text-base text-slate-600 leading-relaxed">
                Dr. Gressell earned his DMD from Temple University School of Dentistry in 2005 and was
                commissioned as a Captain in the U.S. Army, treating service members across three theaters
                of operation from Landstuhl, Germany. After years in private practice in Germany, San
                Antonio, and the Pacific Northwest, he founded Harbor Hill Dentistry &amp; Airway Center
                in Gig Harbor, where he focuses on smile design, dental implants, and airway-centered care.
              </p>
              <p className="mt-3 text-[15px] sm:text-base text-slate-600 leading-relaxed">
                A lifelong innovator — and inventor of the LinguaGuard suction device — Dr. Gressell
                shares the tools he believes move dentistry forward. Asha is one of them.
              </p>

              <div className="mt-5 flex flex-wrap justify-center md:justify-start gap-2">
                {credentials.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-100 px-3 py-1 text-[11px] font-semibold text-blue-800">
                    <Check className="w-3 h-3 text-blue-500" />
                    {c}
                  </span>
                ))}
              </div>

              <div className="mt-7">
                <Button
                  onClick={scrollToForm}
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white h-12 px-7 text-base font-semibold rounded-full shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all hover:-translate-y-0.5"
                >
                  Request a demo
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ PLATFORM ============ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-50/60 to-transparent" />
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-14">
          <motion.div {...fadeUp} className="text-center mb-10">
            <p className="gr-eyebrow text-[11px] sm:text-xs font-bold uppercase text-blue-600 mb-4">The Asha AI OS</p>
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-[1.12] text-slate-900 max-w-3xl mx-auto">
              The World's Only <span className="text-blue-600">AI Operating System</span> for Dentistry.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Asha AI combines over 10 software and 20 AI products all into one system that connects
              with the goal of running as much of your practice on autopilot as possible.
            </p>
            <div className="gr-hairline h-px max-w-xs mx-auto mt-8" />
          </motion.div>

          {/* Two panels: softwares left, AI products right */}
          <div className="grid lg:grid-cols-2 gap-5">
            {/* Left: 11 Softwares */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="bg-white rounded-3xl border border-slate-200 shadow-lg shadow-slate-900/[0.04] overflow-hidden h-full flex flex-col"
            >
              <div className="flex items-center gap-3 p-5 border-b border-slate-100">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 border border-indigo-200 flex items-center justify-center">
                  <Monitor className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">11 Softwares</h3>
                  <p className="text-xs text-slate-400">Replaces your entire tech stack</p>
                </div>
                <span className="ml-auto text-[10px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-full px-2.5 py-1">
                  ALL INCLUDED
                </span>
              </div>
              <div className="p-3 flex-1 flex flex-col justify-between gap-1.5">
                {softwares.map((s, i) => (
                  <motion.div
                    key={s.label}
                    initial={{ opacity: 0, x: -14 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-30px" }}
                    transition={{ duration: 0.35, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
                    className="flex items-center gap-3 rounded-xl bg-slate-50 border border-slate-100 px-3.5 py-2.5 hover:bg-indigo-50/60 hover:border-indigo-200 transition-colors"
                  >
                    <span className="text-[10px] font-bold text-indigo-300 tabular-nums w-5">{String(i + 1).padStart(2, "0")}</span>
                    <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-white border border-indigo-100 flex items-center justify-center">
                      <s.icon className="w-3.5 h-3.5 text-indigo-600" />
                    </div>
                    <span className="text-[13px] font-semibold text-slate-800">{s.label}</span>
                    <Check className="ml-auto w-3.5 h-3.5 text-indigo-400" />
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right: 20 AI Products */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="relative h-full"
            >
              {/* Ambient animated glow behind AI panel */}
              <div className="gr-glow absolute -inset-4 bg-gradient-to-br from-blue-200/50 via-sky-100/40 to-indigo-200/50 rounded-[32px] blur-2xl -z-10" />

              <div className="bg-white rounded-3xl border border-blue-200 shadow-lg shadow-blue-900/[0.06] overflow-hidden h-full flex flex-col">
                <div className="flex items-center gap-3 p-5 border-b border-blue-100 bg-gradient-to-r from-blue-50/80 to-transparent">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-md shadow-blue-200 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">20 AI Products</h3>
                    <p className="text-xs text-slate-400">Does the work your staff would</p>
                  </div>
                  <span className="ml-auto inline-flex items-center gap-2 text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-2.5 py-1">
                    <span className="relative flex w-1.5 h-1.5">
                      <span className="gr-ping absolute inline-flex w-full h-full rounded-full bg-blue-500" />
                      <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-blue-600" />
                    </span>
                    ALWAYS ON
                  </span>
                </div>
                <div className="p-3 flex-1 grid grid-cols-2 auto-rows-fr gap-1.5">
                  {aiProducts.map((label, i) => (
                    <motion.div
                      key={label}
                      initial={{ opacity: 0, y: 10, scale: 0.96 }}
                      whileInView={{ opacity: 1, y: 0, scale: 1 }}
                      viewport={{ once: true, margin: "-30px" }}
                      transition={{ type: "spring", stiffness: 260, damping: 22, delay: i * 0.045 }}
                      style={{ animationDelay: `${i * 0.7}s` }}
                      className="gr-ai-chip flex items-center gap-2.5 rounded-xl border border-blue-100/80 px-3 py-2.5 hover:border-blue-300 transition-colors"
                    >
                      <div className="flex-shrink-0 w-6 h-6 rounded-md bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                        <Bot className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-[12.5px] font-semibold text-slate-700 leading-tight">{label}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ DEMO FORM ============ */}
      <section ref={formRef} className="relative scroll-mt-8 max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <motion.div {...fadeUp} className="text-center mb-8">
          <p className="gr-eyebrow text-[11px] sm:text-xs font-bold uppercase text-blue-600 mb-4">Take Dr. Gressell up on it</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            Want a demo? Fill out this form.
          </h2>
          <p className="mt-3 text-slate-600 max-w-md mx-auto text-sm sm:text-base">
            Tell us a little about your practice and you'll pick a time that works on the next page.
          </p>
        </motion.div>

        <motion.div {...fadeUp} className="max-w-xl mx-auto">
          <div className="bg-white rounded-3xl border border-blue-100 shadow-xl shadow-blue-900/[0.05] overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-sky-400 via-blue-600 to-indigo-500" />
            <form className="p-6 sm:p-8 space-y-4" onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="firstName" className="text-xs font-semibold text-slate-700">First name *</Label>
                  <Input id="firstName" placeholder="Jane" value={formData.firstName} onChange={handleInputChange} required
                    className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-10 text-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lastName" className="text-xs font-semibold text-slate-700">Last name *</Label>
                  <Input id="lastName" placeholder="Doe" value={formData.lastName} onChange={handleInputChange} required
                    className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-10 text-sm" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs font-semibold text-slate-700">Work email *</Label>
                <Input id="email" type="email" placeholder="jane@practice.com" value={formData.email} onChange={handleInputChange} required
                  className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-10 text-sm" />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="phone" className="text-xs font-semibold text-slate-700">Phone number *</Label>
                <Input id="phone" type="tel" placeholder="(555) 000-0000" value={formData.phone} onChange={handleInputChange} required
                  className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-10 text-sm" />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="practiceName" className="text-xs font-semibold text-slate-700">Practice name *</Label>
                <Input id="practiceName" placeholder="Bright Smile Dental" value={formData.practiceName} onChange={handleInputChange} required
                  className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-10 text-sm" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="providers" className="text-xs font-semibold text-slate-700">Providers *</Label>
                  <Select onValueChange={(value) => handleSelectChange("providers", value)} required>
                    <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-10 text-sm">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-2">1-2</SelectItem>
                      <SelectItem value="3-5">3-5</SelectItem>
                      <SelectItem value="6-10">6-10</SelectItem>
                      <SelectItem value="11+">11+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="role" className="text-xs font-semibold text-slate-700">Role *</Label>
                  <Select onValueChange={(value) => handleSelectChange("role", value)} required>
                    <SelectTrigger className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 h-10 text-sm">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner">Owner/Dentist</SelectItem>
                      <SelectItem value="manager">Office Manager</SelectItem>
                      <SelectItem value="front-desk">Front Desk</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="message" className="text-xs font-semibold text-slate-700">
                  Anything else? <span className="text-slate-400 font-normal">(Optional)</span>
                </Label>
                <Textarea id="message" placeholder="Tell us about your current challenges..." value={formData.message} onChange={handleInputChange}
                  className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 min-h-[64px] resize-none transition-all text-sm" />
              </div>

              {submitError && (
                <p className="text-xs font-semibold text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{submitError}</p>
              )}

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-base font-semibold rounded-xl shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Continue to schedule your demo
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>

              <p className="text-[11px] text-center text-slate-400">
                No commitment required. You'll pick a demo time on the next page.
              </p>
            </form>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-blue-100/80 bg-white/60">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-center">
          <p className="text-[11px] text-slate-400">
            Prepared for colleagues of Dr. Keith Gressell · Harbor Hill Dentistry &amp; Airway Center
          </p>
          <a href="https://asha.dental" className="text-[11px] font-semibold text-blue-600 hover:text-blue-700">
            asha.dental
          </a>
        </div>
      </footer>
    </div>
  );
};

export default DrGressellPage;
